/* eslint-disable @typescript-eslint/no-explicit-any */
const copyProperty = (
  to: any,
  from: any,
  property: any,
  ignoreNonConfigurable: any
) => {
  // `Function#length` should reflect the parameters of `to` not `from` since we keep its body.
  // `Function#prototype` is non-writable and non-configurable so can never be modified.
  if (property === 'length' || property === 'prototype') {
    return;
  }

  // `Function#arguments` and `Function#caller` should not be copied. They were reported to be present in `Reflect.ownKeys` for some devices in React Native (#41), so we explicitly ignore them here.
  if (property === 'arguments' || property === 'caller') {
    return;
  }

  const toDescriptor = Object.getOwnPropertyDescriptor(to, property);
  const fromDescriptor = Object.getOwnPropertyDescriptor(from, property);

  if (!canCopyProperty(toDescriptor, fromDescriptor) && ignoreNonConfigurable) {
    return;
  }

  if (!fromDescriptor) throw new TypeError(fromDescriptor);
  Object.defineProperty(to, property, fromDescriptor);
};

// `Object.defineProperty()` throws if the property exists, is not configurable and either:
// - one its descriptors is changed
// - it is non-writable and its value is changed
const canCopyProperty = function (toDescriptor: any, fromDescriptor: any) {
  return (
    toDescriptor === undefined ||
    toDescriptor.configurable ||
    (toDescriptor.writable === fromDescriptor.writable &&
      toDescriptor.enumerable === fromDescriptor.enumerable &&
      toDescriptor.configurable === fromDescriptor.configurable &&
      (toDescriptor.writable || toDescriptor.value === fromDescriptor.value))
  );
};

const changePrototype = (to: (...arguments_: any) => any, from: any) => {
  const fromPrototype = Object.getPrototypeOf(from);
  if (fromPrototype === Object.getPrototypeOf(to)) {
    return;
  }

  Object.setPrototypeOf(to, fromPrototype);
};

const wrappedToString = (withName: string, fromBody: string) =>
  `/* Wrapped ${withName}*/\n${fromBody}`;

const toStringDescriptor = Object.getOwnPropertyDescriptor(
  Function.prototype,
  'toString'
);
const toStringName = Object.getOwnPropertyDescriptor(
  Function.prototype.toString,
  'name'
);

// We call `from.toString()` early (not lazily) to ensure `from` can be garbage collected.
// We use `bind()` instead of a closure for the same reason.
// Calling `from.toString()` early also allows caching it in case `to.toString()` is called several times.
const changeToString = (to: any, from: any, name: string) => {
  const withName = name === '' ? '' : `with ${name.trim()}() `;
  const newToString = wrappedToString.bind(null, withName, from.toString());
  // Ensure `to.toString.toString` is non-enumerable and has the same `same`
  if (!toStringName) throw new TypeError(toStringName);
  Object.defineProperty(newToString, 'name', toStringName);
  Object.defineProperty(to, 'toString', {
    ...toStringDescriptor,
    value: newToString,
  });
};

type TOptions = {
  /**
      Skip modifying [non-configurable properties](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/getOwnPropertyDescriptor#Description) instead of throwing an error.
  
      @default false
      */
  readonly ignoreNonConfigurable?: boolean;
};

function mimicFunction<
  ArgumentsType extends unknown[],
  ReturnType,
  FunctionType extends (...arguments_: ArgumentsType) => ReturnType
>(
  to: (...arguments_: ArgumentsType) => ReturnType,
  from: FunctionType,
  ignoreNonConfigurable?: TOptions
): FunctionType {
  const { name } = to;

  for (const property of Reflect.ownKeys(from)) {
    copyProperty(to, from, property, ignoreNonConfigurable);
  }

  changePrototype(to, from);
  changeToString(to, from, name);

  return to as FunctionType;
}

type AnyFunction = (...arguments_: readonly any[]) => unknown;

const cacheStore = new WeakMap<AnyFunction, CacheStorage<any, any>>();
const cacheTimerStore = new WeakMap<AnyFunction, Set<number>>();

type CacheStorageContent<ValueType> = {
  data: ValueType;
  maxAge: number;
};

type CacheStorage<KeyType, ValueType> = {
  has: (key: KeyType) => boolean;
  get: (key: KeyType) => CacheStorageContent<ValueType> | undefined;
  set: (key: KeyType, value: CacheStorageContent<ValueType>) => void;
  delete: (key: KeyType) => void;
  clear?: () => void;
};

type Options<FunctionToMemoize extends AnyFunction, CacheKeyType> = {
  /**
	Milliseconds until the cache entry expires.

	@default Infinity
	*/
  readonly maxAge?: number;

  /**
	Determines the cache key for storing the result based on the function arguments. By default, __only the first argument is considered__ and it only works with [primitives](https://developer.mozilla.org/en-US/docs/Glossary/Primitive).

	A `cacheKey` function can return any type supported by `Map` (or whatever structure you use in the `cache` option).

	You can have it cache **all** the arguments by value with `JSON.stringify`, if they are compatible:

	```
	import memoize from 'memoize';

	memoize(function_, {cacheKey: JSON.stringify});
	```

	Or you can use a more full-featured serializer like [serialize-javascript](https://github.com/yahoo/serialize-javascript) to add support for `RegExp`, `Date` and so on.

	```
	import memoize from 'memoize';
	import serializeJavascript from 'serialize-javascript';

	memoize(function_, {cacheKey: serializeJavascript});
	```

	@default arguments_ => arguments_[0]
	@example arguments_ => JSON.stringify(arguments_)
	*/
  readonly cacheKey?: (
    arguments_: Parameters<FunctionToMemoize>
  ) => CacheKeyType;

  /**
	Use a different cache storage. Must implement the following methods: `.has(key)`, `.get(key)`, `.set(key, value)`, `.delete(key)`, and optionally `.clear()`. You could for example use a `WeakMap` instead or [`quick-lru`](https://github.com/sindresorhus/quick-lru) for a LRU cache.

	@default new Map()
	@example new WeakMap()
	*/
  readonly cache?: CacheStorage<CacheKeyType, ReturnType<FunctionToMemoize>>;
};

/**
[Memoize](https://en.wikipedia.org/wiki/Memoization) functions - An optimization used to speed up consecutive function calls by caching the result of calls with identical input.

@param fn - The function to be memoized.

@example
```
import memoize from 'memoize';

let index = 0;
const counter = () => ++index;
const memoized = memoize(counter);

memoized('foo');
//=> 1

// Cached as it's the same argument
memoized('foo');
//=> 1

// Not cached anymore as the arguments changed
memoized('bar');
//=> 2

memoized('bar');
//=> 2
```
*/
export default function memoize<
  FunctionToMemoize extends AnyFunction,
  CacheKeyType
>(
  fn: FunctionToMemoize,
  {
    cacheKey,
    cache = new Map(),
    maxAge,
  }: Options<FunctionToMemoize, CacheKeyType> = {}
): FunctionToMemoize {
  if (maxAge === 0) {
    return fn;
  }

  if (typeof maxAge === 'number') {
    const maxSetIntervalValue = 2_147_483_647;
    if (maxAge > maxSetIntervalValue) {
      throw new TypeError(
        `The \`maxAge\` option cannot exceed ${maxSetIntervalValue}.`
      );
    }

    if (maxAge < 0) {
      throw new TypeError(
        'The `maxAge` option should not be a negative number.'
      );
    }
  }

  const memoized = function (
    this: any,
    ...arguments_: Parameters<FunctionToMemoize>
  ): ReturnType<FunctionToMemoize> {
    const key = cacheKey
      ? cacheKey(arguments_)
      : (arguments_[0] as CacheKeyType);

    const cacheItem = cache.get(key);
    if (cacheItem) {
      return cacheItem.data;
    }

    const result = fn.apply(this, arguments_) as ReturnType<FunctionToMemoize>;

    cache.set(key, {
      data: result,
      maxAge: maxAge ? Date.now() + maxAge : Number.POSITIVE_INFINITY,
    });

    if (typeof maxAge === 'number' && maxAge !== Number.POSITIVE_INFINITY) {
      const timer = setTimeout(() => {
        cache.delete(key);
      }, maxAge);

      timer.unref?.();

      const timers = cacheTimerStore.get(fn) ?? new Set<number>();
      timers.add(timer as unknown as number);
      cacheTimerStore.set(fn, timers);
    }

    return result;
  } as FunctionToMemoize;

  mimicFunction(memoized, fn, {
    ignoreNonConfigurable: true,
  });

  cacheStore.set(memoized, cache);

  return memoized;
}

/**
@returns A [decorator](https://github.com/tc39/proposal-decorators) to memoize class methods or static class methods.

@example
```
import {memoizeDecorator} from 'memoize';

class Example {
	index = 0

	@memoizeDecorator()
	counter() {
		return ++this.index;
	}
}

class ExampleWithOptions {
	index = 0

	@memoizeDecorator({maxAge: 1000})
	counter() {
		return ++this.index;
	}
}
```
*/
export function memoizeDecorator<
  FunctionToMemoize extends AnyFunction,
  CacheKeyType
>(options: Options<FunctionToMemoize, CacheKeyType> = {}) {
  const instanceMap = new WeakMap();

  return (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ): void => {
    const input = target[propertyKey];  

    if (typeof input !== 'function') {
      throw new TypeError('The decorated value must be a function');
    }

    delete descriptor.value;
    delete descriptor.writable;

    descriptor.get = function () {
      if (!instanceMap.has(this)) {
        const value = memoize(input, options) as FunctionToMemoize;
        instanceMap.set(this, value);
        return value;
      }

      return instanceMap.get(this) as FunctionToMemoize;
    };
  };
}

/**
Clear all cached data of a memoized function.

@param fn - The memoized function.
*/
export function memoizeClear(fn: AnyFunction): void {
  const cache = cacheStore.get(fn);
  if (!cache) {
    throw new TypeError("Can't clear a function that was not memoized!");
  }

  if (typeof cache.clear !== 'function') {
    throw new TypeError("The cache Map can't be cleared!");
  }

  cache.clear();

  for (const timer of cacheTimerStore.get(fn) ?? []) {
    clearTimeout(timer);
  }
}
